import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Play, Pause, Square, RefreshCw, Repeat, Users, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { gtplSaathiAPI } from '../services/api';
import useDarkReady from '../theme/useDarkReady';
import AreaMultiSelect from '../components/common/AreaMultiSelect';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading, Progress, SwitchRow } from '../components/gtpl/GtplKit';

// GTPL Bulk Change Order: same package, same language, D30 for every eligible regular
// customer. Real money leaves the GTPL wallet. Spec: docs/superpowers/specs/2026-09-28-bulk-change-order-design.md
const ACTIVE = ['running', 'paused', 'waiting_session'];
const STATUS_TONE = { done: 'ok', skipped: 'mute', failed: 'bad', unconfirmed: 'warn', submitting: 'info' };
const RUN_LABEL = { running: 'Running', paused: 'Paused', waiting_session: 'Waiting for a fresh GTPL login', completed: 'Completed', cancelled: 'Stopped', failed: 'Failed' };
const RUN_TONE = { running: 'info', paused: 'warn', waiting_session: 'warn', completed: 'ok', cancelled: 'mute', failed: 'bad' };
const inr = (n) => `₹${(Math.round((n || 0) * 100) / 100).toLocaleString('en-IN')}`;

// Switch for the automatic monthly renewal-date alignment (backend gtplCycleAlignService).
function AutoAlignCard() {
  const [st, setSt] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { gtplSaathiAPI.getAutoAlign().then((r) => setSt(r.data.data)).catch(() => toast.error('Could not load the automatic alignment setting')); }, []);
  const flip = async (enabled) => {
    setSaving(true);
    try { const r = await gtplSaathiAPI.setAutoAlign(enabled); setSt(r.data.data); toast.success(enabled ? 'Automatic alignment ON' : 'Automatic alignment OFF'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    setSaving(false);
  };
  const next = st?.cycleEnd && new Date(`${st.cycleEnd}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return (
    <Panel icon={Repeat} tone="adv" title="Automatic renewal-date alignment">
      <SwitchRow title={`Automatic alignment: ${st ? (st.enabled ? 'ON' : 'OFF') : '…'}`} checked={!!st?.enabled} disabled={!st || saving} onChange={flip}>
        Keeps SPBP0042 regular customers on one GTPL renewal date. WhatsApp on the common end day, change orders the next day at 10:00.
        {next ? ` Next common end: ${next}.` : ' The date is read from GTPL at the next check.'}
      </SwitchRow>
    </Panel>
  );
}

export default function GtplBulkChangeOrderPage() {
  useDarkReady();
  const [summary, setSummary] = useState(null);
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState({ rows: [], total: 0 });
  const [rowFilter, setRowFilter] = useState('');
  const [unticked, setUnticked] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [areaSel, setAreaSel] = useState(() => new Set()); // ticked areas; empty = all
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = run && ACTIVE.includes(run.status);

  const loadSummary = useCallback(() => {
    gtplSaathiAPI.getChangeOrderSummary().then((r) => setSummary(r.data.data)).catch((e) => toast.error(e.response?.data?.message || 'Could not load eligible customers'));
  }, []);
  const loadRun = useCallback(async () => {
    try {
      const r = await gtplSaathiAPI.getChangeOrderLatest();
      const latest = r.data.data;
      setRun(latest);
      if (latest) {
        const rr = await gtplSaathiAPI.getChangeOrderRows({ runId: latest._id, status: rowFilter || undefined });
        setRows(rr.data.data);
      }
    } catch (e) { /* the next poll retries */ }
  }, [rowFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRun(); }, [loadRun]);
  // When a run finishes, the eligible list changes (ordered customers drop out): reload it.
  const runStatus = run?.status;
  useEffect(() => { if (runStatus && !ACTIVE.includes(runStatus)) loadSummary(); }, [runStatus, loadSummary]);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(loadRun, 4000);
    return () => clearInterval(t);
  }, [active, loadRun]);

  const areas = useMemo(() => [...new Set((summary?.eligible || []).map((c) => c.area).filter(Boolean))].sort(), [summary]);
  const areaCounts = useMemo(() => (summary?.eligible || []).reduce((m, c) => { if (c.area) m[c.area] = (m[c.area] || 0) + 1; return m; }, {}), [summary]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (summary?.eligible || []).filter((c) => (!areaSel.size || areaSel.has(c.area))
      && (!q || c.name.toLowerCase().includes(q) || c.accountNumber.includes(q) || (c.address || '').toLowerCase().includes(q) || (c.area || '').toLowerCase().includes(q)));
  }, [summary, search, areaSel]);
  const filtered = !!(areaSel.size || search.trim());
  // "Only these": tick exactly the customers currently shown, untick everyone else.
  const onlyShown = () => {
    const keep = new Set(shown.map((c) => c.accountNumber));
    setUnticked(new Set(summary.eligible.map((c) => c.accountNumber).filter((a) => !keep.has(a))));
  };
  const selectedCount = (summary?.eligible.length || 0) - unticked.size;
  const toggle = (acc) => setUnticked((s) => { const n = new Set(s); if (n.has(acc)) n.delete(acc); else n.add(acc); return n; });

  const start = async () => {
    setBusy(true);
    try {
      // The server runs only these accounts, and only if they are still eligible when it starts.
      const includeAccounts = summary.eligible.map((c) => c.accountNumber).filter((a) => !unticked.has(a));
      await gtplSaathiAPI.startChangeOrderRun({ includeAccounts });
      toast.success('Bulk change order started');
      setConfirming(false);
      loadRun();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start');
    } finally { setBusy(false); }
  };
  const control = async (fn, msg) => {
    try { await fn(); toast.success(msg); loadRun(); } catch (e) { toast.error(e.response?.data?.message || 'Action failed'); }
  };

  return (
    <Screen>
      <PageHead icon={Repeat} tone="warn" title="Bulk Change Order"
        sub="Same package, same language, 30 days, for regular Cable TV customers. Each order deducts from the GTPL wallet." />

      <AutoAlignCard />

      {run && (
        <Panel icon={Activity} tone={RUN_TONE[run.status] || 'mute'} title={`Latest run · ${RUN_LABEL[run.status] || run.status}`}
          right={<>
            {run.status === 'running' && <button type="button" className="btn btn-warning btn-sm" onClick={() => control(gtplSaathiAPI.pauseChangeOrderRun, 'Pausing after the current customer')}><Pause size={14} /> Pause</button>}
            {run.status === 'paused' && <button type="button" className="btn btn-primary btn-sm" onClick={() => control(gtplSaathiAPI.resumeChangeOrderRun, 'Resumed')}><Play size={14} /> Resume</button>}
            {active && <button type="button" className="btn btn-danger btn-sm" onClick={() => control(gtplSaathiAPI.stopChangeOrderRun, 'Stopping')}><Square size={14} /> Stop</button>}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadRun}><RefreshCw size={14} /> Refresh</button>
          </>}>
          {(run.pauseReason || run.failMessage) && (
            <div style={{ marginBottom: 12 }}><Notice tone={run.status === 'failed' ? 'bad' : 'warn'}>{run.pauseReason || run.failMessage}</Notice></div>
          )}
          <Stats>
            <Stat tone="info" value={`${run.processed} / ${run.total}`} label="Processed" />
            <Stat tone="ok" value={run.counts?.done || 0} label="Done" />
            <Stat tone="mute" value={run.counts?.skipped || 0} label="Skipped" />
            <Stat tone={run.counts?.failed ? 'bad' : 'mute'} value={run.counts?.failed || 0} label="Failed" />
            <Stat tone={run.counts?.unconfirmed ? 'warn' : 'mute'} value={run.counts?.unconfirmed || 0} label="Unconfirmed" />
            <Stat tone="adv" value={inr(run.spent)} label="Spent" />
          </Stats>
          {active && <Progress value={run.processed || 0} max={run.total || 0} tone={RUN_TONE[run.status]} />}

          <div className="gx-actions" style={{ margin: '14px 0 10px' }}>
            <select id="co-filter" className="form-control" style={{ width: 'auto' }} value={rowFilter} onChange={(e) => setRowFilter(e.target.value)} aria-label="Show results">
              <option value="">All results</option><option value="done">Done</option><option value="skipped">Skipped</option><option value="failed">Failed</option><option value="unconfirmed">Unconfirmed</option>
            </select>
            <span className="text-muted text-small">{rows.total} rows</span>
          </div>
          {rows.rows.length === 0 ? <Empty title="No results yet" /> : (
            <div className="gx-list">
              {rows.rows.map((r) => (
                <Item key={r._id} tone={STATUS_TONE[r.status] === 'mute' ? undefined : STATUS_TONE[r.status]}
                  title={r.name} sub={<><span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span> · {r.plan || '—'}{r.language ? ` · ${r.language}` : ''}</>}
                  right={<Pill tone={STATUS_TONE[r.status] || 'mute'} dot>{r.status}</Pill>}
                  meta={<>
                    <span>Before: {r.before?.start ? `${r.before.start} → ${r.before.end}` : '—'}</span>
                    <span>After: {r.after?.start ? `${r.after.start} → ${r.after.end}` : '—'}</span>
                    <span>Deducted: {r.deducted != null ? inr(r.deducted) : '—'}</span>
                    {r.message && <span>{r.message}</span>}
                  </>} />
              ))}
            </div>
          )}
        </Panel>
      )}

      {!active && (
        <Panel icon={Users} tone="info" title="Eligible customers"
          sub="Regular, active Cable TV on ROYAL, ROYAL HD, POWER+ or PRIME, not change-ordered in the last 25 days. Customers GTPL would reject are skipped during the run without charge."
          right={<button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadSummary}><RefreshCw size={14} /> Refresh list</button>}>
          {!summary ? <Loading /> : (
            <div className="gx-form">
              <div className="gx-actions">
                <b>{selectedCount} of {summary.eligible.length} selected</b>
                <span className="text-muted text-small">Pauses if the GTPL wallet drops below {inr(summary.walletFloor)}.</span>
              </div>
              {Object.keys(summary.excluded).length > 0 && (
                <span className="text-muted text-small">Not eligible: {Object.entries(summary.excluded).map(([k, v]) => `${k} (${v})`).join(', ')}</span>
              )}
              <div className="gx-actions">
                <input className="form-control" style={{ flex: '1 1 220px' }} placeholder="Search name, account or address" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search eligible customers" />
                <AreaMultiSelect areas={areas} counts={areaCounts} value={areaSel} onChange={setAreaSel} />
              </div>
              <div className="gx-actions">
                {filtered && <button type="button" className="btn btn-outline btn-sm" onClick={onlyShown}>Only these {shown.length}</button>}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUnticked(new Set())}>Select all</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUnticked(new Set(summary.eligible.map((c) => c.accountNumber)))}>Select none</button>
              </div>
              <div className="gx-list" style={{ maxHeight: 380, overflowY: 'auto' }}>
                {shown.map((c) => (
                  <label key={c.id} className={`gx-item${!unticked.has(c.accountNumber) ? ' is-selected' : ''}`}
                    style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '9px 12px' }}>
                    <input type="checkbox" aria-label={`Include ${c.name}`} checked={!unticked.has(c.accountNumber)} onChange={() => toggle(c.accountNumber)} style={{ marginTop: 3 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b>{c.name}</b> <span className="font-mono text-small text-muted">{c.accountNumber}</span>
                      <div className="text-small text-muted">{c.address || '—'}{c.area ? ` · ${c.area}` : ''}</div>
                    </span>
                    <Pill tone="info">{c.package}</Pill>
                  </label>
                ))}
                {shown.length === 0 && <Empty title="No customers match" />}
              </div>

              <div className="gx-sticky">
                {!confirming ? (
                  <button type="button" className="btn btn-primary" disabled={selectedCount === 0} onClick={() => setConfirming(true)}>
                    <Play size={15} /> Start bulk change order ({selectedCount})
                  </button>
                ) : (
                  <Notice tone="bad" title={`Place real change orders for ${selectedCount} customer${selectedCount === 1 ? '' : 's'}?`}
                    actions={<>
                      <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={start}>{busy ? 'Starting…' : 'Yes, start'}</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
                    </>}>
                    Each one deducts from the GTPL wallet.
                  </Notice>
                )}
              </div>
            </div>
          )}
        </Panel>
      )}
    </Screen>
  );
}
